import { useEffect, useState } from 'react';
import { installNativeBridgeHost, hasNativeCapability, nativeShellPlatform } from '../utils/nativeShell';
import { NATIVE_BRIDGE_VERSION } from '../utils/nativeBridgeWeb';
import { parseNativeLayout, type LayoutSnapshotV1 } from '../utils/nativeLayout';
import { verticalChromeLayout } from '../utils/verticalChrome';

/** Each document requests a full replay; layout messages are optional in envelope v1. */
export function useNativeLayout(): LayoutSnapshotV1 | null {
  const [layout, setLayout] = useState<LayoutSnapshotV1 | null>(null);
  useEffect(() => {
    const revisions = new Map<string, number>();
    const request = () => {
      if (!hasNativeCapability('layoutGeometry')) return;
      window.ReactNativeWebView?.postMessage(JSON.stringify({ source: 'bilt-web', v: NATIVE_BRIDGE_VERSION, payload: { type: 'GET_LAYOUT' } }));
    };
    const dispose = installNativeBridgeHost(event => {
      if (event.type !== 'LAYOUT_CHANGED' || !hasNativeCapability('layoutGeometry')) return;
      const next = parseNativeLayout(event.layout);
      if (!next || next.revision <= (revisions.get(next.sceneId) ?? -1)) return;
      revisions.set(next.sceneId, next.revision);
      setLayout(next);
    });
    window.addEventListener('bilt-native-shell-ready', request);
    request();
    return () => { dispose(); window.removeEventListener('bilt-native-shell-ready', request); };
  }, []);
  return layout;
}

/** Keep safe insets live; old binaries and browsers keep their existing env() fallback. */
export function NativeLayoutStyles() {
  const layout = useNativeLayout();
  useEffect(() => {
    if (!layout) return;
    const element = document.documentElement;
    for (const edge of ['top', 'right', 'bottom', 'left'] as const)
      element.style.setProperty(`--bilt-safe-${edge}`, `${layout.safeInsets[edge]}px`);
    element.dataset.biltLayout = '1';
    const dock = verticalChromeLayout(layout, nativeShellPlatform() === 'ios_app' && hasNativeCapability('layoutGeometry'));
    if (dock) {
      element.dataset.biltVerticalEdge = dock.edge;
      element.style.setProperty('--bilt-dock-width', `${dock.width}px`);
      element.style.setProperty('--bilt-chrome-font-scale', String(layout.fontScale));
    } else {
      delete element.dataset.biltVerticalEdge;
      element.style.removeProperty('--bilt-dock-width');
      element.style.removeProperty('--bilt-chrome-font-scale');
    }
  }, [layout]);
  useEffect(() => () => {
    for (const edge of ['top', 'right', 'bottom', 'left']) document.documentElement.style.removeProperty(`--bilt-safe-${edge}`);
    delete document.documentElement.dataset.biltLayout;
    delete document.documentElement.dataset.biltVerticalEdge;
    document.documentElement.style.removeProperty('--bilt-dock-width');
    document.documentElement.style.removeProperty('--bilt-chrome-font-scale');
  }, []);
  return null;
}
